import { API_BASE_URL, browserPublicShareFetch } from "./api-base.ts";
import type { StrategyRuleDSL } from "./strategy-builder.ts";
import type { BacktestFormState } from "./types.ts";
import {
  SHARE_RESULT_MAX_RESPONSE_BYTES,
  SHARE_RESULT_TOKEN_PATTERN,
  parsePublicSharedResult,
  type PublicSharedResult,
} from "../shared-result-contract.ts";

const ATTRIBUTION_PATTERN = /^att_[A-Za-z0-9_-]{43}$/;
const SHARE_ID_PATTERN = /^shr_[A-Za-z0-9_-]{43}$/;
const BLOCKED_STORAGE_KEY = "stoptrading.blockedSharedResults.v1";
const DEFAULT_REPORT_SUPPORT_CONTACT = "dattn2101@gmail.com";
const REPORT_SUPPORT_CONTACT_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/;
const IMPORTABLE_TIMEFRAMES = new Set<BacktestFormState["timeframe"]>([
  "5m", "15m", "30m", "1h", "4h", "1d",
]);

export type SharedStrategyAttribution = {
  shareID: string;
  attributionEventID: string;
};

export type SharedStrategyDescriptor = {
  token: string;
  shareID: string;
  attributionEventID?: string;
};

export type SharedResultReportReceipt = {
  detailsRetained?: boolean;
  coalesced?: boolean;
  supportContact?: string;
};

export type SharedResultReportSupportLink = {
  contact: string;
  href: string;
};

export function sharedStrategyIsImportable(
  result: Pick<PublicSharedResult, "reproduction" | "shareID">,
): boolean {
  const reproduction = result.reproduction;
  if (!reproduction || !result.shareID) return false;
  // Symbols are intentionally not checked against the bundled fallback list:
  // Market Discovery also accepts a backend-provided dynamic catalog, so a
  // static gate here would reject newly supported markets. Public parsing
  // already bounds and sanitizes the symbol before it reaches this function.
  return IMPORTABLE_TIMEFRAMES.has(reproduction.timeframe as BacktestFormState["timeframe"]);
}

export function sharedResearchActionAvailability(
  result: Pick<PublicSharedResult, "researchNetworkEnabled" | "moderationEnabled" | "reproduction" | "shareID">,
): { duplicate: boolean; report: boolean } {
  return {
    duplicate: result.researchNetworkEnabled === true && sharedStrategyIsImportable(result),
    // Moderation remains independently available for public summary links
    // even when the research-network/attribution experiment is disabled.
    report: result.moderationEnabled === true,
  };
}

type InitialShareLocation = Pick<Location, "hash" | "pathname" | "search">;
type InitialShareHistory = Pick<History, "replaceState" | "state">;

export function consumeInitialSharedStrategyDescriptor(
  location: InitialShareLocation,
  history: InitialShareHistory,
): SharedStrategyDescriptor | null {
  if (!location.hash.startsWith("#")) return null;
  const params = new URLSearchParams(location.hash.slice(1));
  if (!params.has("reproduce")) return null;
  // Strip the raw bearer before validating companion IDs. Even a truncated or
  // corrupted handoff must not survive long enough for telemetry to inspect it.
  history.replaceState(
    history.state,
    "",
    `${location.pathname}${location.search}`,
  );
  const token = params.get("reproduce") || "";
  const shareID = params.get("share") || "";
  const attributionEventID = params.get("attribution") || "";
  if (!SHARE_RESULT_TOKEN_PATTERN.test(token)
    || !SHARE_ID_PATTERN.test(shareID)
    || (attributionEventID && !ATTRIBUTION_PATTERN.test(attributionEventID))) return null;
  return {
    token,
    shareID,
    ...(attributionEventID ? { attributionEventID } : {}),
  };
}

// Remember only the presence of a handoff, never its bearer. Startup defaults
// must not overwrite an explicit link after the hash has been privacy-scrubbed.
export const hadInitialSharedStrategyInput = typeof window !== "undefined"
  && new URLSearchParams(window.location.hash.slice(1)).has("reproduce");

let pendingInitialDescriptor = typeof window === "undefined"
  ? null
  : consumeInitialSharedStrategyDescriptor(window.location, window.history);

export function takeInitialSharedStrategyDescriptor(): SharedStrategyDescriptor | null {
  const value = pendingInitialDescriptor;
  pendingInitialDescriptor = null;
  return value;
}

async function boundedJSON(response: Response): Promise<unknown> {
  const declared = Number(response.headers.get("content-length"));
  if (Number.isFinite(declared) && declared > SHARE_RESULT_MAX_RESPONSE_BYTES) {
    throw new Error("response_too_large");
  }
  const text = await response.text();
  if (new TextEncoder().encode(text).byteLength > SHARE_RESULT_MAX_RESPONSE_BYTES) {
    throw new Error("response_too_large");
  }
  return JSON.parse(text) as unknown;
}

export async function fetchSharedStrategy(
  descriptor: SharedStrategyDescriptor,
  signal?: AbortSignal,
): Promise<PublicSharedResult> {
  const response = await browserPublicShareFetch(
    `${API_BASE_URL}/api/v2/shareable-results/public`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Share-Token": descriptor.token,
      },
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal,
    },
  );
  if (!response.ok) throw new Error("shared_strategy_unavailable");
  const result = parsePublicSharedResult(await boundedJSON(response));
  if (!result || !sharedStrategyIsImportable(result) || result.shareID !== descriptor.shareID) {
    throw new Error("shared_strategy_invalid");
  }
  return result;
}

export function applySharedStrategy(
  current: BacktestFormState,
  result: PublicSharedResult,
): BacktestFormState {
  const reproduction = result.reproduction;
  if (!reproduction || !sharedStrategyIsImportable(result)) return current;
  const assumptions = reproduction.assumptions;
  return {
    ...current,
    symbol: reproduction.symbol,
    provider: reproduction.provider,
    market: reproduction.market,
    timeframe: reproduction.timeframe as BacktestFormState["timeframe"],
    period: "Custom",
    strategyMode: "builder",
    strategyMethod: undefined, unsupportedMethodExecution: undefined,
    strategyName: reproduction.strategyLabel,
    prompt: "Visual Strategy Builder",
    strategyDSL: reproduction.strategyDSL as unknown as StrategyRuleDSL,
    commissionRate: assumptions.commissionPercent,
    slippageBps: assumptions.slippageBps,
    positionSizePct: assumptions.positionSizePercent,
    executionModel: assumptions.executionModel,
    marginMode: assumptions.marginMode,
    leverage: assumptions.leverage,
    maintenanceMarginPercent: assumptions.maintenanceMarginPercent,
    fundingRatePercent: assumptions.fundingRatePercent,
    fundingIntervalHours: assumptions.fundingIntervalHours,
    liquidationFeePercent: assumptions.liquidationFeePercent,
    tradeDirection: assumptions.tradeDirection,
    exitMode: assumptions.exitMode,
    stopLossPct: assumptions.stopLossPercent,
    takeProfitPct: assumptions.takeProfitPercent,
    useAI: false,
    startDate: assumptions.startDate.slice(0, 10),
    endDate: assumptions.endDate.slice(0, 10),
  };
}

export async function createSharedAttribution(
  token: string,
  signal?: AbortSignal,
): Promise<SharedStrategyAttribution> {
  const clientEventID = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const response = await browserPublicShareFetch(
    `${API_BASE_URL}/api/v2/shareable-results/public/attributions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Share-Token": token,
      },
      body: JSON.stringify({ event: "duplicate_started", client_event_id: clientEventID }),
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal,
    },
  );
  if (!response.ok) throw new Error("shared_attribution_failed");
  const value = await boundedJSON(response);
  if (!value || typeof value !== "object") throw new Error("shared_attribution_invalid");
  const record = value as Record<string, unknown>;
  if (record.accepted !== true || typeof record.share_id !== "string"
    || !SHARE_ID_PATTERN.test(record.share_id)
    || typeof record.attribution_event_id !== "string"
    || !ATTRIBUTION_PATTERN.test(record.attribution_event_id)) {
    throw new Error("shared_attribution_invalid");
  }
  return {
    shareID: record.share_id,
    attributionEventID: record.attribution_event_id,
  };
}

export function sharedStrategyDestination(
  token: string,
  shareID: string,
  attribution: SharedStrategyAttribution | null,
  locale: string,
): string {
  const prefix = locale === "en" ? "" : `/${locale}`;
  const fragment = new URLSearchParams({
    reproduce: token,
    share: shareID,
  });
  if (attribution?.shareID === shareID) {
    fragment.set("attribution", attribution.attributionEventID);
  }
  return `https://backtest.click${prefix}/backtest#${fragment.toString()}`;
}

/**
 * Attribution must never become a dependency of the user's primary action.
 * A disabled/failed attribution endpoint simply produces a handoff without an
 * attribution ID; the imported strategy remains fully usable.
 */
export async function sharedStrategyDestinationWithBestEffortAttribution(
  token: string,
  shareID: string,
  locale: string,
  createAttribution: (
    token: string,
    signal?: AbortSignal,
  ) => Promise<SharedStrategyAttribution> = createSharedAttribution,
  options: { timeoutMilliseconds?: number; signal?: AbortSignal } = {},
): Promise<string> {
  const timeoutMilliseconds = Math.max(1, Math.min(options.timeoutMilliseconds ?? 750, 2_000));
  if (options.signal?.aborted) throw new DOMException("Aborted", "AbortError");
  const controller = new AbortController();
  let timeoutID: ReturnType<typeof setTimeout> | undefined;
  let removeAbortListener: () => void = () => {};
  const timeout = new Promise<null>((resolve) => {
    timeoutID = setTimeout(() => resolve(null), timeoutMilliseconds);
  });
  const externallyAborted = new Promise<never>((_resolve, reject) => {
    const abort = () => {
      controller.abort();
      reject(new DOMException("Aborted", "AbortError"));
    };
    options.signal?.addEventListener("abort", abort, { once: true });
    removeAbortListener = () => options.signal?.removeEventListener("abort", abort);
  });
  let attribution: SharedStrategyAttribution | null = null;
  try {
    attribution = await Promise.race([
      createAttribution(token, controller.signal),
      timeout,
      externallyAborted,
    ]);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    attribution = null;
  } finally {
    if (timeoutID !== undefined) clearTimeout(timeoutID);
    removeAbortListener();
    controller.abort();
  }
  return sharedStrategyDestination(token, shareID, attribution, locale);
}

export async function reportSharedResult(
  token: string,
  reason: "misleading" | "spam" | "unsafe" | "other",
  details?: string,
): Promise<SharedResultReportReceipt> {
  const idempotencyKey = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `web-report-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const response = await browserPublicShareFetch(
    `${API_BASE_URL}/api/v2/shareable-results/public/reports`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Idempotency-Key": idempotencyKey,
        "X-Share-Token": token,
      },
      body: JSON.stringify({ reason, ...(details?.trim() ? { details: details.trim() } : {}) }),
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
    },
  );
  if (!response.ok) throw new Error("shared_report_failed");
  const value = await boundedJSON(response);
  if (!value || typeof value !== "object") throw new Error("shared_report_invalid");
  const record = value as Record<string, unknown>;
  if (record.accepted !== true || typeof record.report_id !== "string" || !record.report_id) {
    throw new Error("shared_report_invalid");
  }
  if (record.details_retained !== undefined && typeof record.details_retained !== "boolean") {
    throw new Error("shared_report_invalid");
  }
  if (record.coalesced !== undefined && typeof record.coalesced !== "boolean") {
    throw new Error("shared_report_invalid");
  }
  const supportContact = typeof record.support_contact === "string"
    ? safeReportSupportContact(record.support_contact)
    : undefined;
  return {
    ...(record.details_retained === undefined
      ? {}
      : { detailsRetained: record.details_retained }),
    ...(record.coalesced === undefined ? {} : { coalesced: record.coalesced }),
    ...(supportContact ? { supportContact } : {}),
  };
}

function safeReportSupportContact(candidate?: string): string | undefined {
  const value = candidate?.trim();
  if (!value || value.length > 254 || !REPORT_SUPPORT_CONTACT_PATTERN.test(value)) return undefined;
  return value;
}

/**
 * Builds a support handoff from public identifiers only. The bearer token is
 * intentionally not accepted by this API, so it cannot leak into Mail drafts.
 */
export function sharedResultReportSupportLink(
  candidateContact: string | undefined,
  shareID: string | undefined,
  acceptedWithoutDetails: boolean,
): SharedResultReportSupportLink {
  const contact = safeReportSupportContact(candidateContact) || DEFAULT_REPORT_SUPPORT_CONTACT;
  const reference = shareID && SHARE_ID_PATTERN.test(shareID) ? shareID : "summary-only";
  const query = new URLSearchParams({
    subject: "Backtest shared-result report",
    body: acceptedWithoutDetails
      ? `Shared result reference: ${reference}\nThe in-app report was accepted, but optional details were not retained.`
      : `Shared result reference: ${reference}\nThe in-app report could not be delivered.`,
  });
  return { contact, href: `mailto:${contact}?${query.toString()}` };
}

async function tokenDigest(token: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function blockedValues(): string[] {
  try {
    const value = JSON.parse(window.localStorage.getItem(BLOCKED_STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value.filter((item) => typeof item === "string").slice(-200) : [];
  } catch {
    return [];
  }
}

export async function blockSharedResult(token: string): Promise<void> {
  const digest = await tokenDigest(token);
  const values = [...new Set([...blockedValues(), digest])].slice(-200);
  try {
    window.localStorage.setItem(BLOCKED_STORAGE_KEY, JSON.stringify(values));
  } catch {
    // The current page still hides the result even when persistent storage is
    // unavailable in a hardened/private browser context.
  }
}

export async function sharedResultIsBlocked(token: string): Promise<boolean> {
  return blockedValues().includes(await tokenDigest(token));
}
